import axios from "../utils/axios";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Modal, notification, Select, Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { useState } from "react";
import "antd/es/modal/style";
import "antd/es/slider/style";
import { useMutation } from "react-query";

const { TextArea } = Input;

const ASPECT_BY_TYPE = {
  eseal: 2 / 2,
  signature: 2 / 1,
  initials: 1 / 1,
};

export const UserStampModal = ({ visible, onCancel, userID, next }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const type = Form.useWatch("type", form);
  const aspect = ASPECT_BY_TYPE[type] ?? 1;

  const { mutate, isLoading } = useMutation({
    mutationKey: "upload_user_image",
    mutationFn: async (formData) => {
      const { description, shortname, type, file } = formData;

      const form = new FormData();
      form.append("file", file);

      // **Important:** Use encodeURIComponent for query parameters
      const encodedDescription = encodeURIComponent(description);
      const encodedShortname = encodeURIComponent(shortname);

      return await axios.post(
        // Ensure 'type' is used in the path, and parameters are encoded
        `/admin/api/user/uploadimg/${userID}/${type}?description=${encodedDescription}&shortname=${encodedShortname}`,
        form,
        { formData: true },
      );
    },
    onSuccess: ({ success, data }) => {
      if (success) {
        notification.success({
          description: "Successfully uploaded.",
          placement: "topRight",
          duration: 3,
        });
        onCancel();
        setFile(null);
        form.resetFields();
        next && next();
      } else {
        throw new Error();
      }
    },
    onError: (error) => {
      notification.error({
        description: error.response?.data?.message || "Failed to upload.",
        placement: "topRight",
        duration: 5,
      });
    },
  });

  const onChange = async (upload) => {
    const cropped = upload.file.originFileObj;
    // This should correctly read the cropped file data and update 'file' state for preview
    getBase64(cropped, (url) => {
      setFile(url);
    });

    form.setFieldValue("file", cropped);
  };

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  return (
    <Modal title="Stamp add" open={visible} onCancel={onCancel} footer={null}>
      <Form layout="vertical" onFinish={mutate} form={form}>
        <Form.Item
          label="Short name"
          name="shortname"
          rules={[
            {
              required: true,
              message: "Please input your shortname!",
            },
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please input your description!",
            },
          ]}>
          <TextArea />
        </Form.Item>
        <Form.Item
          label="Image type"
          name="type"
          rules={[
            {
              required: true,
              message: "Please select your sign type!",
            },
          ]}
          initialValue="eseal">
          <Select>
            <Select.Option value="eseal">
              <img alt="eseal_icon" src="eseal.png" style={{ height: 20, marginRight: 10 }} />
              eseal
            </Select.Option>
            <Select.Option value="signature">
              <img alt="signature_icon" src="signature.png" style={{ height: 20, marginRight: 10 }} />
              signature
            </Select.Option>
            <Select.Option value="initials">
              <img alt="initials_icon" src="initials.png" style={{ height: 20, marginRight: 10 }} />
              initials
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="file"
          label="File"
          className="flex justify-between"
          rules={[
            {
              required: true,
              message: "Please upload your file!",
            },
          ]}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <ImgCrop key={type} quality={1} rotationSlider aspect={aspect}>
              <Upload maxCount={1} accept="image/*" showUploadList={false} onChange={onChange}>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </ImgCrop>
            {file && <Image alt="user_file" src={file} height={200} />}
          </div>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
